import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  Typography,
} from "@mui/material";

import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const linkStyle = {
  color: "#f8fafc",
  "&:hover": {
    color: "#a99bff",
  },
};

export default function Footer({
  language = "pl",
  email = "Michal.Jaskulski@allworks.pl",
  phone = "+48 602 209 858",
  companyName = "Allworks sp. z o.o.",
  nip = "635-184-83-80",
  city = "Mikołów, Polska",
}) {
  const isPl = language === "pl";

  const contactItems = [
    {
      icon: <EmailOutlinedIcon />,
      content: (
        <Link href={`mailto:${email}`} underline="none" sx={linkStyle}>
          {email}
        </Link>
      ),
    },
    {
      icon: <PhoneOutlinedIcon />,
      content: (
        <Link
          href={`tel:${phone.replace(/[^\d+]/g, "")}`}
          underline="none"
          sx={linkStyle}
        >
          {phone}
        </Link>
      ),
    },
    {
      icon: <BusinessOutlinedIcon />,
      content: (
        <Box>
          <Typography component="h3" sx={{ color: "#fff", fontWeight: 600 }}>
            {companyName}
          </Typography>
          {nip && (
            <Typography sx={{ color: "#aab8d0", fontSize: 14 }}>
              NIP: {nip}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      icon: <LocationOnOutlinedIcon />,
      content: <Typography sx={{ color: "inherit" }}>{city}</Typography>,
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        mt: 8,
        color: "#f8fafc",
        background:
          "radial-gradient(ellipse at 100% 0%, rgba(79,70,229,0.28) 0%, transparent 34%), linear-gradient(120deg, #10233f 0%, #172f52 55%, #10213d 100%)",
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 6 } }}>
        {/* Logo i opis */}
        <Grid
          container
          spacing={3}
          alignItems="center"
          sx={{ mb: { xs: 4, md: 5 } }}
        >
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  flex: "0 0 auto",
                  display: "grid",
                  placeItems: "center",
                  border: "1px solid rgba(169,155,255,0.65)",
                  borderRadius: "16px",
                  color: "#a99bff",
                  fontSize: 27,
                  fontWeight: 700,
                  letterSpacing: "-2px",
                }}
              >
                MJ
              </Box>

              <Box>
                <Typography
                  component="h2"
                  sx={{
                    color: "#fff",
                    fontSize: { xs: 21, md: 25 },
                    fontWeight: 300,
                    lineHeight: 1.2,
                  }}
                >
                  Michał Jaskulski
                </Typography>

                <Typography
                  sx={{
                    mt: 0.6,
                    color: "#b8c5dc",
                    fontSize: 12,
                    letterSpacing: "2.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Fullstack React Developer
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Typography
              component="h3"
              sx={{
                color: "#e7edf8",
                fontSize: { xs: 16, md: 20 },
                lineHeight: 1.8,
              }}
            >
              {isPl
                ? "Aplikacje webowe  ·  Systemy ERP  ·  Wsparcie IT"
                : "Web applications  ·  ERP systems  ·  IT support"}
            </Typography>

            <Box
              sx={{
                mt: 1.2,
                width: 105,
                height: 4,
                borderRadius: 5,
                background: "linear-gradient(90deg, #8b7cff, #5547e8)",
              }}
            />
          </Grid>
        </Grid>

        {/* Dane kontaktowe */}
        <Grid
          container
          spacing={{ xs: 3, md: 2 }}
          alignItems="center"
          sx={{ mb: { xs: 4, md: 5 } }}
        >
          {contactItems.map((item, index) => (
            <Grid item xs={12} sm={6} md key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    flex: "0 0 auto",
                    display: "grid",
                    placeItems: "center",
                    color: "#c2baff",
                    border: "1px solid rgba(139,124,255,0.5)",
                    borderRadius: "12px",
                    backgroundColor: "rgba(255,255,255,0.045)",
                    "& svg": {
                      fontSize: 23,
                    },
                  }}
                >
                  {item.icon}
                </Box>

                <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
                  {item.content}
                </Box>
              </Box>
            </Grid>
          ))}

          {/* Przycisk kontaktowy */}
          <Grid item xs={12} sm={6} md="auto">
            <Button
              component="a"
              href="/kontakt"
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              sx={{
                minHeight: 46,
                px: 2.5,
                borderRadius: "10px",
                borderColor: "#8173ff",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "#b2a8ff",
                  backgroundColor: "rgba(129,115,255,0.12)",
                },
              }}
            >
              {isPl ? "Napisz do mnie" : "Contact me"}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(190,205,230,0.25)" }} />

        {/* Dolna część stopki */}
        <Box
          sx={{
            pt: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography sx={{ color: "#c3cee0", fontSize: 14 }}>
            © {new Date().getFullYear()} Michał Jaskulski / {companyName}.{" "}
            {isPl ? "Wszelkie prawa zastrzeżone." : "All rights reserved."}
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {/*}
            <Link href="/polityka-prywatnosci" underline="none" sx={linkStyle}>
              {isPl ? "Polityka prywatności" : "Privacy policy"}
            </Link>

            <Typography sx={{ color: "#7185a5" }}>|</Typography>

            <Link href="/cookies" underline="none" sx={linkStyle}>
              {isPl ? "Pliki cookies" : "Cookies"}
            </Link>
            */}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
